import { SimpleTooltip } from '@bambi/component-library/src/components/Tooltip/SimpleTooltip';
import * as Checkbox from '@radix-ui/react-checkbox';
import { CheckIcon, DividerHorizontalIcon } from '@radix-ui/react-icons';
import { styled } from '@stitches/react';
import type { ReactNode } from 'react';
import { useId } from 'react';

export function IndeterminateCheckbox({
  indeterminate,
  checked,
  onChange,
  disabled,
  id,
  label,
  tooltip,
}: {
  indeterminate?: boolean;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  id?: string;
  label?: ReactNode;
  tooltip?: string;
}) {
  const generatedId = useId();
  const checkboxId = id ?? generatedId;
  const isIndeterminate = !!indeterminate && !checked;
  const checkbox = (
    <CheckboxRoot
      id={checkboxId}
      className="focus:ring-indigo-500 focus:outline-mint disabled:cursor-not-allowed disabled:opacity-50"
      checked={isIndeterminate ? 'indeterminate' : checked}
      onCheckedChange={(state) => onChange(state === true)}
      disabled={disabled}
    >
      <Checkbox.Indicator>
        {checked ? (
          <CheckIcon data-testid="check-icon" />
        ) : isIndeterminate ? (
          <DividerHorizontalIcon data-testid="divider-horizontal-icon" />
        ) : null}
      </Checkbox.Indicator>
    </CheckboxRoot>
  );

  if (!label) {
    if (!tooltip) {
      return checkbox;
    }

    return <SimpleTooltip trigger={<div>{checkbox}</div>} content={tooltip} />;
  }

  const wrappedCheckbox = (
    <div className="flex items-center gap-2">
      {checkbox}
      <label
        htmlFor={checkboxId}
        className={`text-sm font-medium text-gray-700 ${
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        }`}
      >
        {label}
      </label>
    </div>
  );

  if (!tooltip) {
    return wrappedCheckbox;
  }

  return (
    <SimpleTooltip trigger={<div>{wrappedCheckbox}</div>} content={tooltip} />
  );
}

const CheckboxRoot = styled(Checkbox.Root, {
  /* Primary/50 */
  background: '#F9F5FF',
  /* Primary/600 */
  border: '1px solid #7F56D9',
  borderRadius: '4px',
  backgroundColor: 'white',
  width: 16,
  height: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': { backgroundColor: '#F9F5FF' },
});
